/**
 * Contract for `GET /api/monitoring/alerts/` (MIN-228) — staff-only ingest
 * observability.
 *
 * It answers exactly two questions: how many alerts the platform accepted, and
 * at which timestamp stage / producer / scope delay or missing data accumulates.
 * It is NOT the queue, not business analytics, not a model-quality report, not a
 * producer availability check, and not a health score. The single time basis is
 * `platform_accepted_at`, in UTC.
 *
 * Completeness here means "the required timestamps are present" — a factual
 * data-quality statement, not a verdict about whether anything is healthy.
 */

export type AlertMonitoringStageCode =
  | "model_inference"
  | "capture_to_ingestion"
  | "ingestion_delay"
  | "live_publication";

/** Data-flow order. All four are always shown together — never one at a time. */
export const MONITORING_STAGE_ORDER: AlertMonitoringStageCode[] = [
  "model_inference",
  "capture_to_ingestion",
  "ingestion_delay",
  "live_publication",
];

/** Translation keys for the stage names; the station resolves them at render. */
export const MONITORING_STAGE_LABEL_KEYS: Record<
  AlertMonitoringStageCode,
  | "monitoringStages.modelInference"
  | "monitoringStages.captureToIngestion"
  | "monitoringStages.ingestionDelay"
  | "monitoringStages.livePublication"
> = {
  model_inference: "monitoringStages.modelInference",
  capture_to_ingestion: "monitoringStages.captureToIngestion",
  ingestion_delay: "monitoringStages.ingestionDelay",
  live_publication: "monitoringStages.livePublication",
};

export type AlertMonitoringScopeLevel =
  | "platform"
  | "organization"
  | "location"
  | "machine"
  | "device";

/**
 * Alert-level completeness counts. `complete_required_chain_*` is a secondary
 * readout, NOT a score — these must never be combined into one number.
 */
export interface AlertMonitoringSummary {
  accepted_alert_count: number;
  complete_required_chain_count: number;
  complete_required_chain_share: number | null;
  missing_expected_timestamp_alert_count: number;
  missing_expected_timestamp_alert_share: number | null;
  pending_expected_timestamp_alert_count: number;
  pending_expected_timestamp_alert_share: number | null;
  clock_skew_alert_count: number;
  clock_skew_alert_share: number | null;
}

/**
 * Per-stage metrics. Every percentile and share is BACKEND-COMPUTED — the client
 * never derives one, because a percentile recomputed from a truncated or
 * bucketed sample is a different number wearing the same name.
 */
export interface AlertMonitoringStageMetric {
  code: AlertMonitoringStageCode;
  total_count: number;
  applicable_count: number;
  not_applicable_count: number;
  valid_count: number;
  missing_count: number;
  clock_skew_count: number;
  pending_count: number;
  valid_share_of_applicable: number | null;
  missing_share_of_applicable: number | null;
  clock_skew_share_of_applicable: number | null;
  pending_share_of_applicable: number | null;
  p50_ms: number | null;
  p95_ms: number | null;
}

export interface AlertMonitoringBreakdownItem extends AlertMonitoringSummary {
  organization_id: number | null;
  organization_label: string | null;
  location_id: number | null;
  location_label: string | null;
  machine_id: number | null;
  machine_label: string | null;
  device_mxid: string | null;
  capture_to_ingestion_p95_ms?: number | null;
  live_publication_missing_count?: number;
  live_publication_missing_share?: number | null;
}

export interface AlertMonitoringComponentRow extends AlertMonitoringSummary {
  source_component: string;
  stages: AlertMonitoringStageMetric[];
}

export interface AlertMonitoringTrendPoint {
  start_time: string;
  end_time: string;
  /** Null when the bucket held no valid samples — a GAP, never a zero. */
  p95_ms: number | null;
  total_count: number;
  applicable_count: number;
  not_applicable_count: number;
  valid_count: number;
  missing_count: number;
  clock_skew_count: number;
  pending_count: number;
}

export interface AlertMonitoringResponse {
  as_of: string;
  /** Latest acceptance in this scope regardless of period. Null = none ever. */
  scope_latest_accepted_at: string | null;
  scope: {
    level: AlertMonitoringScopeLevel;
    organization_id: number | null;
    location_id: number | null;
    machine_ids: number[];
    device_mxids: string[];
    source_components: string[];
    type_in: string[];
  };
  period: {
    start_time: string;
    end_time: string;
    duration_seconds: number;
    time_basis: "platform_accepted_at";
  };
  coverage: {
    /** When live-publication timing began being expected; null = no expectation. */
    live_expected_from: string | null;
    /** Records younger than this are N/A rather than missing. Backend-owned. */
    live_grace_seconds: number | null;
  };
  summary: AlertMonitoringSummary;
  stages: AlertMonitoringStageMetric[];
  breakdown: {
    dimension: "organization" | "location" | "machine" | "device" | null;
    items: AlertMonitoringBreakdownItem[];
    /** True when only the top results are present — never a complete ranking. */
    truncated: boolean;
  };
  component_breakdown: AlertMonitoringComponentRow[];
  trend: {
    /** Drives the axis tick format AND the displayed bucket size. */
    bucket_seconds: number;
    series: Array<{ stage: AlertMonitoringStageCode; points: AlertMonitoringTrendPoint[] }>;
  };
}

export interface AlertMonitoringScopeInput {
  organization_id?: number;
  location_id?: number;
  machine_ids?: number[];
  device_mxids?: string[];
  source_components?: string[];
  type_in?: string[];
}

export interface AlertMonitoringParams extends AlertMonitoringScopeInput {
  /** Required inclusive boundary (ISO with Z). */
  start_time: string;
  /** Omitted for rolling presets — the backend owns `as_of`. */
  end_time?: string;
}

/** Rolling presets only. Monitoring has no arbitrary date-range picker. */
export type MonitoringPreset = "1h" | "6h" | "24h" | "7d";

export const MONITORING_PRESETS: Array<{ id: MonitoringPreset; label: string; seconds: number }> = [
  { id: "1h", label: "1 hour", seconds: 3_600 },
  { id: "6h", label: "6 hours", seconds: 21_600 },
  { id: "24h", label: "24 hours", seconds: 86_400 },
  { id: "7d", label: "7 days", seconds: 604_800 },
];

export const DEFAULT_MONITORING_PRESET: MonitoringPreset = "6h";

export const SEVEN_DAYS_SECONDS = 604_800;

/** Auto-refresh cadence ceiling. */
export const MONITORING_REFRESH_MS = 120_000;
